import { useQuery, useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import usePagination from './usePagination';
import {
    getStressTests,
    fetchStressTest,
    createStressTest,
    uploadStressTestFile
} from 'services/stresstest';

const id = 'stress-tests';

/**
 * Fetch list of stress tests
 */
export const useStressTests = (patient, group, type, noPagination = false) => {
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination();

    const { isPending, isError, data } = useQuery({
        queryKey: [id, { paginationModel, patient, group, type, noPagination }],
        queryFn: async () => {
            if (noPagination) {
                let allData = [];
                let pageToken = null;
                let hasNext = true;

                while (hasNext) {
                    const resp = await getStressTests({
                        pageLimit: 50,
                        pageToken,
                        patient,
                        group,
                        type
                    });

                    allData = [...allData, ...(resp?.data || [])];

                    const nextToken = resp?.headers?.['next-page-token'];
                    if (nextToken) {
                        pageToken = nextToken;
                    } else {
                        hasNext = false;
                    }
                }

                return { data: allData };
            }

            return getStressTests({
                pageLimit: paginationModel.pageSize,
                pageToken: currentToken(paginationModel.page),
                patient,
                group,
                type
            });
        }
    });

    useEffect(() => {
        if (!noPagination && data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [data, noPagination]);

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    };
};

/**
 * Fetch single stress test
 */
export const useStressTest = (uuid, enabled = true) => {
    const { isPending, isError, data } = useQuery({
        queryKey: ['stress-test', uuid],
        queryFn: () => fetchStressTest(uuid),
        enabled: enabled && Boolean(uuid)
    });

    return {
        data: data?.data || null,
        isLoading: isPending,
        error: isError
    };
};

/**
 * Create stress test mutation
 */
export const useCreateStressTest = () =>
    useMutation({
        mutationFn: createStressTest
    });

/**
 * Upload stress test file mutation
 */
export const useUploadStressTestFile = () =>
    useMutation({
        mutationFn: uploadStressTestFile
    });
